import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const monoFont = "'IBM Plex Mono', monospace";

const AUTH_TEMPLATES = ['signup', 'invite', 'magiclink', 'recovery', 'email_change', 'reauthentication'];

interface EmailLog {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

function formatDate(d: string) {
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function truncate(s: string, n = 140) {
  return s.length > n ? `${s.substring(0, n)}...` : s;
}

export function SAEmails() {
  const [rows, setRows] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sentIds, setSentIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const { data } = await supabase
      .from('email_send_log')
      .select('*')
      .eq('status', 'dlq')
      .order('created_at', { ascending: false });
    setRows((data as any[] | null) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleRequeue = async (row: EmailLog) => {
    if (!row.message_id) { setError('registro sem message_id'); setTimeout(() => setError(null), 4000); return; }
    setSendingId(row.id);
    setError(null);
    const queue_name = AUTH_TEMPLATES.includes(row.template_name) ? 'auth_emails' : 'transactional_emails';
    const { data, error: fnError } = await supabase.functions.invoke('requeue-dlq-email', {
      body: { queue_name, message_id: row.message_id },
    });
    setSendingId(null);
    if (fnError || (data as any)?.error) {
      setError((data as any)?.error ?? fnError?.message ?? 'falha ao reenviar');
      setTimeout(() => setError(null), 4000);
      return;
    }
    setSentIds(prev => [...prev, row.id]);
    load();
  };

  if (loading) {
    return <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.3)', padding: '40px 0' }}>Carregando...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: monoFont, fontSize: 18, fontWeight: 300, color: '#F7F6F2' }}>E-mails</div>
        <button
          onClick={() => { setLoading(true); load(); }}
          style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 14px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.6)', cursor: 'pointer' }}
        >
          Atualizar
        </button>
      </div>

      {error && (
        <div style={{ fontFamily: monoFont, fontSize: 12, color: '#FF3F2D', marginBottom: 12 }}>{error}</div>
      )}

      {rows.length === 0 ? (
        <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.3)', textAlign: 'center', padding: '40px 0' }}>Nenhum e-mail na fila de falhas.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(row => {
            const sent = sentIds.includes(row.id);
            return (
              <div key={row.id} style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: '#F7F6F2' }}>{row.recipient_email}</span>
                  {sent ? (
                    <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: '#00E87A', border: '0.5px solid #00E87A', padding: '4px 8px' }}>Reenviado</span>
                  ) : (
                    <button
                      onClick={() => handleRequeue(row)}
                      disabled={sendingId === row.id}
                      style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '6px 12px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.6)', cursor: sendingId === row.id ? 'default' : 'pointer', opacity: sendingId === row.id ? 0.5 : 1, whiteSpace: 'nowrap' }}
                    >
                      {sendingId === row.id ? 'Reenviando...' : 'Reenviar'}
                    </button>
                  )}
                </div>
                <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.45)' }}>
                  {row.template_name} · {formatDate(row.created_at)}
                </div>
                {row.error_message && (
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.45)', marginTop: 6 }}>
                    {truncate(row.error_message)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
